/* eslint-disable @nrwl/nx/enforce-module-boundaries */
/** Copyright 2023 Midas Healthcare Solutions - All Rights Reserved **/
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthActions, UserActions } from '@midas/shared/data-access';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { take, timer } from 'rxjs';
import { AutoLogoutDialogComponent } from './auto-logout-dialog.component';
import { LocalStorageAuthRefreshTokenKey } from '@midas/shared/common';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../../../../apps/web/src/environments/environment';

interface IJWTToken {
  exp: number;
}

@Injectable({ providedIn: 'root' })
export class IdleTimerService {
  private EXPIRE_TIME = '_expireTime';
  private TIMEOUT_MINUTES = environment.TIMEOUT_SESSION_MINUTES || 60;
  private DISPLAY_DIALOG_REMAINING_SECONDS = 60;
  private isDialogVisible = false;
  private pauseListener = false;
  private dialogWasShowed = false;
  private sessionKeepAliveValue = environment.SESSION_KEEP_ALIVE || false;
  private usingMockData = environment.useMocks || false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private eventHandler: any;
  private timeoutTracker!: ReturnType<typeof setTimeout>;

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private actions: Actions,
    private router: Router,
  ) {
    this.eventHandler = this.updateExpiredTime.bind(this);
  }

  initializeIdleTimer() {
    this.actions.pipe(ofType(UserActions.getProfileSuccess), take(1)).subscribe(() => {
      console.log('Idle timer started.');
      this.setExpiredTime();
      this.startListeners();
      this.startInterval();
    });
  }

  getSecondsRemaining() {
    const expiredTime = parseInt(localStorage.getItem(this.EXPIRE_TIME) || '0');
    return expiredTime - Date.now() > 0 ? (expiredTime - Date.now()) / 1000 : 0;
  }

  private startListeners() {
    window.addEventListener('click', this.eventHandler);
    window.addEventListener('keydown', this.eventHandler);
  }

  private stopListeners() {
    window.removeEventListener('click', this.eventHandler);
    window.removeEventListener('keydown', this.eventHandler);
  }

  private setExpiredTime() {
    localStorage.setItem(this.EXPIRE_TIME, (Date.now() + this.TIMEOUT_MINUTES * 60 * 1000).toString());
  }

  /**
   * Validate if it is neccesary refresh the token.
   * @returns A Boolean that indicate if the token need to be refresh.
   */
  private checkifTokenNeedsRefresh() {
    const token = localStorage.getItem('midas-token');
    const decoded = token && (jwtDecode(token) as IJWTToken);
    if (!decoded) return;

    const tokenDateFormat = new Date(decoded.exp * 1000);
    const tokenTimeFormat5Minutesbefore = new Date(tokenDateFormat.getTime() - 300000);
    const token5minBeforeDateFormat = new Date(tokenTimeFormat5Minutesbefore);
    return token5minBeforeDateFormat.getTime() < new Date().getTime();
  }

  private shouldKeepSessionAlive() {
    if (this.sessionKeepAliveValue) return true;

    const checkActualUrlArrray = this.router.url.split('/');
    const actualUrl = checkActualUrlArrray[checkActualUrlArrray.length - 1];

    return actualUrl === 'views' || actualUrl === 'statistics';
  }

  private async startInterval() {
    this.updateExpiredTime();

    const idleTimer = timer(0, 1000).subscribe(async () => {
      const remainingSeconds = this.getSecondsRemaining();
      if (
        this.checkifTokenNeedsRefresh() &&
        !this.isDialogVisible &&
        !this.pauseListener &&
        !this.dialogWasShowed &&
        this.shouldKeepSessionAlive()
      ) {
        this.pauseListener = true;
        this.stopListeners();

        this.isDialogVisible = true;
        this.dialogWasShowed = true;
        if (!this.usingMockData) {
          this.store.dispatch(
            AuthActions.refreshToken({ refreshToken: LocalStorageAuthRefreshTokenKey.authRefreshToken }),
          );
        }
        this.updateExpiredTime();
        this.startListeners();
      } else if (
        remainingSeconds < this.DISPLAY_DIALOG_REMAINING_SECONDS &&
        !this.isDialogVisible &&
        !this.pauseListener &&
        !this.dialogWasShowed
      ) {
        this.pauseListener = true;
        this.stopListeners();

        this.isDialogVisible = true;
        this.dialogWasShowed = true;
        this.dialog
          .open(AutoLogoutDialogComponent, {
            panelClass: 'ui-dialog-window',
            maxWidth: '420px',
            disableClose: true,
          })
          .afterClosed()
          .subscribe(() => {
            this.updateExpiredTime();
            this.startListeners();
          });
      }

      if (this.pauseListener && !this.isDialogVisible) {
        await new Promise(() =>
          setTimeout(() => {
            this.pauseListener = false;
            this.dialogWasShowed = false;
          }, 3000),
        );
      }

      if (this.pauseListener && !this.isDialogVisible && this.dialogWasShowed) {
        this.dialogWasShowed = false;
      }

      if (remainingSeconds <= 0) {
        idleTimer.unsubscribe();
        this.cleanUp();
        this.store.dispatch(AuthActions.logoutLocal());
      }
    });
  }

  private updateExpiredTime() {
    if (this.timeoutTracker) {
      clearTimeout(this.timeoutTracker);
    }
    this.timeoutTracker = setTimeout(() => this.setExpiredTime(), 1000);
    this.isDialogVisible = false;
  }

  private cleanUp() {
    this.isDialogVisible = false;
    localStorage.removeItem(this.EXPIRE_TIME);

    this.stopListeners();
  }
}
